import { LockClosedIcon } from "@heroicons/react/20/solid"
import { PlayIcon } from "@heroicons/react/24/solid"
import Link from "next/link"
import { SubtleText } from "@ui/text"
import { CourseWithMeta, isFreeCourse } from "../types"
import { formatDuration } from "../../utils/formatDuration"
import { ThumbnailImage } from "./ThumbnailImage"
import { formatCurrencyFromCents, formatDisplayDate } from "domains/utils/intl"

interface Props {
    course: CourseWithMeta<number>
    slug: string
    priority?: boolean
}

export function Recording({ course, slug, priority = false }: Props) {
    const formatMeta = (
        durationSeconds: number | undefined,
        price: number | null | undefined,
        access: boolean,
        publishedAt?: Date
    ) => {
        const publishedDate = publishedAt ?? new Date()

        const formattedDate = formatDisplayDate(publishedDate)

        const parts: string[] = [formattedDate]

        if (typeof durationSeconds === "number") {
            parts.push(formatDuration(durationSeconds))
        }

        if (price && price > 0) {
            if (access) {
                parts.push("Ready to watch")
            } else {
                parts.push(formatCurrencyFromCents(price))
            }
        } else {
            parts.push("Free")
        }

        return parts.join(" · ")
    }

    if (!course.recording) {
        return null
    }

    const duration = course.recording.durationSeconds ?? undefined
    const price = isFreeCourse(course) ? 0 : (course.recording.stripe?.price.individual as number | null | undefined)
    const publishedAt = course.recording.publishedAt

    return (
        <Link
            key={slug}
            href={`/academy/videos/${slug}${course.access !== false ? "" : "/buy"}`}
            className="group inline-block w-80 shrink-0 snap-mandatory snap-start rounded hover:shadow-sm"
        >
            <div className="relative aspect-video">
                <ThumbnailImage
                    priority={priority}
                    thumbnail={course.thumbnail}
                    title={course.title}
                    className="rounded-t"
                    requireSignedMuxUrl={course.tier !== "free"}
                />
                {course.access === false && (
                    <div className="absolute inset-0 flex items-center justify-center rounded-t bg-black/60">
                        <LockClosedIcon className="size-10 text-white" />
                    </div>
                )}
                <div className="absolute top-2 left-2 rounded-md bg-purple-600 text-white">
                    <PlayIcon className="size-8" />
                </div>
            </div>
            <div className="p-2">
                <h4 className="mb-1 font-semibold group-hover:underline dark:text-gray-100">{course.title}</h4>
                <SubtleText className="text-sm">
                    {formatMeta(duration, price, course.access !== false, publishedAt ?? undefined)}
                </SubtleText>
            </div>
        </Link>
    )
}
